import { forwardRef } from 'react';
import clsx from 'clsx';
import type { NoteMention } from '@shared/flow/notes';
import { splitOnMentions } from './noteFormat';

// Same box and type as Glaze's `Textarea` (size sm) so the text lands exactly under the field's own.
// Copied from Glaze, so `MentionTint.test` pins these padding tokens to the rendered Glaze textarea's.
const BOX =
  'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] text-sm';
// Colour and a ring only: no weight, padding or border, so no glyph moves relative to the field's caret.
const USER = 'text-content-action';
const RECORD = 'rounded-md text-content-primary ring-1 ring-inset ring-border-primary';

/**
 * The composer's visible text. The `<textarea>` stays the source of truth (its
 * text is transparent); this read-only layer behind it paints the same text with
 * `@mention` tokens coloured like the thread bubbles' chips (teammates blue,
 * contacts/accounts outlined). The saved plain text and mention list are untouched.
 */
export const MentionTint = forwardRef<HTMLDivElement, { value: string; mentions: NoteMention[] }>(function MentionTint(
  { value, mentions },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="note-mention-tint"
      className={clsx('pointer-events-none absolute inset-0 overflow-y-auto whitespace-pre-wrap break-words text-content-primary', BOX)}
    >
      {splitOnMentions(value, mentions).map(part =>
        part.mention ? (
          <span key={part.offset} data-testid={`note-mention-${part.mention.kind}`} className={part.mention.kind === 'user' ? USER : RECORD}>
            {part.text}
          </span>
        ) : (
          <span key={part.offset}>{part.text}</span>
        ),
      )}
      {/* A zero-width space: a trailing newline would otherwise collapse and drift the height from the field's. */}
      {'\u200B'}
    </div>
  );
});
